import React from 'react';
import { formatDateDMY } from '../../utils/dateFormat';

/**
 * DocumentViewModal — clean read-only popup showing a PO or PE.
 * Props mirror DocumentPrintView (title, docNumber, docDate, parties, meta,
 * itemColumns, items, totals, notes) plus onClose / onPrint.
 */
const DocumentViewModal = ({
  title,
  docNumber,
  docDate,
  company,
  parties = [],
  meta = [],
  itemColumns = [],
  items = [],
  totals = [],
  notes,
  topSlot,
  watermark,
  onClose,
  onPrint,
}) => {
  const cellValue = (v, col) => {
    if (v === null || v === undefined || v === '') return '-';
    if (col.numeric && typeof v === 'number') return v.toFixed(col.decimals ?? 3);
    return String(v);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="doc-view-modal">
      <div className="bg-white rounded-xl w-full max-w-5xl my-8 relative overflow-hidden">
        {watermark}
        <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <div>
            <h2 className="font-semibold text-lg">{title} · <span className="text-blue-600">{docNumber}</span></h2>
            <p className="text-xs text-gray-500">Date: {docDate ? formatDateDMY(docDate) : '-'}</p>
            {(company?.cr_number || company?.tax_id) && (
              <p className="text-xs text-gray-600 mt-0.5">
                {company?.cr_number ? <>CR No: <b>{company.cr_number}</b></> : null}
                {company?.cr_number && company?.tax_id ? ' · ' : ''}
                {company?.tax_id ? <>VAT No: <b>{company.tax_id}</b></> : null}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {onPrint && (
              <button
                onClick={onPrint}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                data-testid="doc-view-print-btn"
              >
                🖨 Print
              </button>
            )}
            <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50" data-testid="doc-view-close-btn">Close</button>
          </div>
        </div>
        <div className="p-5 space-y-4 text-sm relative">
          {topSlot}
          {parties.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {parties.map((p, i) => (
                <div key={i} className="border rounded p-3 bg-gray-50">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{p.label}</div>
                  {(p.lines || []).filter(Boolean).map((ln, j) => (
                    <div key={j} className="leading-snug">{ln}</div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {meta.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs bg-blue-50 border border-blue-100 rounded p-3">
              {meta.map((m, i) => (
                <div key={i}>
                  <span className="text-gray-500">{m.label}:</span> <span className="ml-1 font-medium">{m.value || '-'}</span>
                </div>
              ))}
            </div>
          )}

          <div className="overflow-x-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-1.5 text-left">#</th>
                  {itemColumns.map((c) => (
                    <th key={c.key} className={`px-2 py-1.5 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={itemColumns.length + 1} className="text-center text-gray-400 py-3">No items</td></tr>
                ) : items.map((it, idx) => {
                  const serials = Array.isArray(it.serial_numbers) ? it.serial_numbers : [];
                  return (
                  <React.Fragment key={idx}>
                  <tr className="border-t">
                    <td className="px-2 py-1.5">{idx + 1}</td>
                    {itemColumns.map((c) => (
                      <td key={c.key} className={`px-2 py-1.5 ${c.align === 'right' ? 'text-right' : ''}`}>
                        {cellValue(it[c.key], c)}
                      </td>
                    ))}
                  </tr>
                  {serials.length > 0 && (
                    <tr className="bg-blue-50/40 border-t border-blue-100">
                      <td></td>
                      <td colSpan={itemColumns.length} className="px-2 py-1 text-xs text-blue-700">
                        <span className="font-semibold">S/N:</span> {serials.join(', ')}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
                })}
              </tbody>
            </table>
          </div>

          {totals.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-auto max-w-md">
              {totals.map((t, i) => (
                <div key={i} className={`flex justify-between border-b py-1 col-span-full ${t.bold ? 'font-bold border-t border-gray-900 text-base mt-1 pt-2' : ''}`}>
                  <span>{t.label}</span>
                  <span>{t.value}</span>
                </div>
              ))}
            </div>
          )}

          {notes && (
            <div className="bg-gray-50 border rounded p-3 text-xs">
              <div className="font-semibold mb-1">Notes</div>
              <div className="whitespace-pre-wrap">{notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewModal;
